import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  RefObject,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Select,
  Card,
  Breadcrumb,
  Table,
  Tabs,
  Checkbox,
  Modal,
  Tag,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FindLocationAssistantReportShiftsResult,
  FindScheduleByIdResult,
  ListEmployeesResult,
  ListLocationsResultDataItem,
  UpdateScheduleShiftDto,
  ToggleScheduleDateIsWaitingForArrangeDto,
} from '../../utils/api-client';
import {
  filter,
  find,
  findIndex,
  groupBy,
  keyBy,
  map,
  mapValues,
  orderBy,
  pickBy,
  size,
  times,
} from 'lodash';
import {
  COOPERATION_STATE,
  COOPERATION_STATE_LABEL,
} from '../../constants/cooperation-state';
import { UseMutateAsyncFunction } from 'react-query';
import {
  useCreateToolBagOwner,
  useExportSchedule,
  useToolBagOwners,
} from '../../utils/api-hooks/pt-schedule.hook';
import html2canvas from 'html2canvas';
import PTReportShiftTable from './pt-report-shift-table';

function hexToRgb(hex: string) {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

function getLuminance(r: number, g: number, b: number) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function getTextColorFromHexBg(hex: string) {
  const [r, g, b] = hexToRgb(hex);
  const luminance = getLuminance(r, g, b);
  return luminance > 186 ? '#000000' : '#FFFFFF'; // 背景亮就用黑字，背景暗就用白字
}

type EmployeeLocationHistoryItem = {
  employeeId: string;
  locationIds: string[];
};

type PTAssignmentComponentProps = {
  schedule: FindScheduleByIdResult | undefined;
  locations: ListLocationsResultDataItem[] | undefined;
  employees: ListEmployeesResult['data'];
  reportShifts: FindLocationAssistantReportShiftsResult['data'];
  employeeLocationHistory: EmployeeLocationHistoryItem[];
  updateShift: UseMutateAsyncFunction<
    unknown,
    unknown,
    { id: string } & UpdateScheduleShiftDto
  >;
  toggleWaitingForArrange: UseMutateAsyncFunction<
    unknown,
    unknown,
    { scheduleId: string } & ToggleScheduleDateIsWaitingForArrangeDto
  >;
  isLoading: boolean;
};

export const PTAssignmentComponent: React.FC<PTAssignmentComponentProps> = ({
  employees = [],
  isLoading,
  locations,
  reportShifts,
  employeeLocationHistory,
  schedule,
  updateShift,
  toggleWaitingForArrange,
}) => {
  const employeeTableRef = useRef(null);
  const locationTableRef = useRef(null);
  const shiftTableRef = useRef(null);

  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // 從 URL 路徑中取得目前的 tab
  const activeTab = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    // 有效的 tab key: employee, location, view, report
    if (['employee', 'location', 'view', 'report'].includes(lastPart)) {
      return lastPart;
    }
    return 'employee';
  }, [location.pathname]);

  const handleTabChange = useCallback(
    (key: string) => {
      navigate(`/schedules/pt/${scheduleId}/assign/${key}`);
    },
    [navigate, scheduleId]
  );

  const [showHolidays, setShowHolidays] = useState<boolean>(false);
  const [showExtraColumns, setShowExtraColumns] = useState<boolean>(false);
  const [batchModalVisible, setBatchModalVisible] = useState<boolean>(false);
  const [batchEmployeeId, setBatchEmployeeId] = useState<string | null>(null);
  const [batchLocationId, setBatchLocationId] = useState<string | undefined>(
    undefined
  );
  const [batchSelectedDates, setBatchSelectedDates] = useState<string[]>([]);
  const [hideInactiveLocations, setHideInactiveLocations] =
    useState<boolean>(true);
  const { mutateAsync: createToolBagOwner } = useCreateToolBagOwner();
  const { isLoading: isExporting } = useExportSchedule();
  const { data: { data: toolBagOwners = [] } = {} } = useToolBagOwners({
    params: [schedule?.startTime, schedule?.endTime],
    enabled: !!schedule,
  });

  const employeeDict = useMemo(() => keyBy(employees, 'id'), [employees]);

  const dateRange = useMemo(() => {
    if (!schedule?.startTime || !schedule?.endTime) return [];

    const dayCount =
      dayjs(schedule.endTime).diff(dayjs(schedule.startTime), 'day') + 1;

    return times(dayCount, (index) => {
      const date = dayjs(schedule.startTime)
        .locale('zh-tw')
        .startOf('day')
        .add(index, 'day');

      return {
        date,
        dateString: date.toISOString(),
        isWeekend: date.day() === 0 || date.day() === 6,
        formattedDate: date.format('MM/DD(dd)'),
      };
    });
  }, [schedule?.startTime, schedule?.endTime]);

  const shiftsByEmployeeAndTime = useMemo(() => {
    if (!schedule?.shifts) return {};

    return mapValues(groupBy(schedule.shifts, 'employeeId'), (employeeShifts) =>
      keyBy(employeeShifts, 'time')
    );
  }, [schedule?.shifts]);

  const shiftsByLocationAndTime = useMemo(() => {
    if (!schedule?.shifts) return {};

    return mapValues(groupBy(schedule.shifts, 'locationId'), (locationShifts) =>
      groupBy(locationShifts, 'time')
    );
  }, [schedule?.shifts]);

  const reportStatusMap = useMemo(() => {
    const map = new Map();

    reportShifts?.forEach((shift) => {
      const key = `${shift.employeeId}_${shift.time}`;
      map.set(key, shift.status);
    });

    return map;
  }, [reportShifts]);

  const reportShiftsByEmployeeId = useMemo(() => {
    return groupBy(reportShifts, 'employeeId');
  }, [reportShifts]);

  // 將員工歷史場域轉換為 Map，key 為 employeeId，value 為該員工曾排班過的 locationId Set
  const employeeLocationHistoryMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    employeeLocationHistory.forEach((item) => {
      map.set(item.employeeId, new Set(item.locationIds));
    });
    return map;
  }, [employeeLocationHistory]);

  const getReportStatus = useCallback(
    (employeeId: string, time: string) => {
      const key = `${employeeId}_${time}`;
      return reportStatusMap.get(key);
    },
    [reportStatusMap]
  );

  const getShiftsByLocationAndTime = useCallback(
    (locationId: string, time: string) => {
      return shiftsByLocationAndTime[locationId]?.[time] || [];
    },
    [shiftsByLocationAndTime]
  );

  const handleLocationShiftsUpdate = useCallback(
    (locationId: string, time: string, newEmployeeIds: string[]) => {
      const locationShifts = getShiftsByLocationAndTime(locationId, time);
      const currentEmployeeIds = locationShifts.map(
        (shift) => shift.employeeId
      );

      const employeeIdsToRemove = locationShifts
        .filter((shift) => !newEmployeeIds.includes(shift.employeeId))
        .map((shift) => shift.employeeId);

      const employeeIdsToAdd = newEmployeeIds.filter(
        (employeeId) => !currentEmployeeIds.includes(employeeId)
      );

      const updates: any[] = [];

      employeeIdsToRemove.forEach((employeeId) => {
        updates.push({
          id: scheduleId!,
          employeeId,
          locationId: undefined,
          time: time,
        });
      });

      employeeIdsToAdd.forEach((employeeId) => {
        updates.push({
          id: scheduleId!,
          employeeId,
          locationId: locationId,
          time: time,
        });
      });

      updates.forEach((update) => updateShift(update));
    },
    [scheduleId, getShiftsByLocationAndTime, updateShift]
  );

  const handleEmployeeShiftUpdate = useCallback(
    (employeeId: string, time: string, locationId: string | undefined) => {
      updateShift({
        id: scheduleId!,
        employeeId,
        locationId,
        time,
      });
    },
    [scheduleId, updateShift]
  );

  const handleBack = useCallback(() => {
    navigate('/schedules/pt');
  }, [navigate]);

  const getIsWaitingForArrange = useCallback(
    (dateString: string, locationId: string) => {
      const dateItem = schedule?.dates?.find(
        (item) => item.time === dateString && item.locationId === locationId
      );
      return dateItem?.isWaitingForArrange || false;
    },
    [schedule?.dates]
  );

  const handleNeedMoreStaffChange = useCallback(
    (locationId: string, dateString: string, checked: boolean) => {
      if (scheduleId) {
        toggleWaitingForArrange({
          scheduleId,
          time: dateString,
          locationId,
        });
      }
    },
    [scheduleId, toggleWaitingForArrange]
  );

  const handleOpenBatchModal = useCallback(
    (employeeId: string) => {
      setBatchEmployeeId(employeeId);
      setBatchLocationId(undefined);
      // 預設選取所有可報班的日期
      const availableDates = filter(
        dateRange,
        ({ dateString }) =>
          getReportStatus(employeeId, dateString) === 'AVAILABLE'
      ).map(({ dateString }) => dateString);
      setBatchSelectedDates(availableDates);
      setBatchModalVisible(true);
    },
    [dateRange, getReportStatus]
  );

  const handleBatchConfirm = useCallback(async () => {
    if (!batchEmployeeId || !scheduleId) return;

    // 使用 for...of 序列化執行更新，避免同時發送多個請求造成部分失敗
    for (const dateString of batchSelectedDates) {
      await updateShift({
        id: scheduleId,
        employeeId: batchEmployeeId,
        locationId: batchLocationId,
        time: dateString,
      });
    }

    setBatchModalVisible(false);
    setBatchEmployeeId(null);
    setBatchLocationId(undefined);
    setBatchSelectedDates([]);
  }, [
    batchEmployeeId,
    batchSelectedDates,
    batchLocationId,
    scheduleId,
    updateShift,
  ]);

  const handleBatchCancel = useCallback(() => {
    setBatchModalVisible(false);
    setBatchEmployeeId(null);
    setBatchLocationId(undefined);
    setBatchSelectedDates([]);
  }, []);

  const batchAvailableDates = useMemo(() => {
    if (!batchEmployeeId) return [];
    return filter(
      dateRange,
      ({ dateString, isWeekend }) =>
        (showHolidays || !isWeekend) &&
        getReportStatus(batchEmployeeId, dateString) === 'AVAILABLE'
    );
  }, [batchEmployeeId, dateRange, showHolidays, getReportStatus]);

  const employeeDataSource = useMemo(() => {
    return orderBy(
      map(pickBy(employees, ['state', 'ACTIVATED']), (employee) => ({
        onBoardingTime: employee.onBoardingTime,
        employeeId: employee.id,
        employeeName: employee.name,
        employeeIdNumber: employee.idNumber,
        isReported: size(reportShiftsByEmployeeId[employee.id]) > 0,
        ...mapValues(
          shiftsByEmployeeAndTime[employee.id] || {},
          (shift) => shift.locationId
        ),
      })),
      ['onBoardingTime'],
      ['asc']
    );
  }, [employees, shiftsByEmployeeAndTime, reportShiftsByEmployeeId]);

  const locationDataSource = useMemo(() => {
    const preferredOrder = [
      '信義區',
      '內湖區',
      '南港區',
      '松山區',
      '大安區',
      null,
    ];
    const filteredLocations = hideInactiveLocations
      ? filter(
          locations || [],
          (location) => location.status !== COOPERATION_STATE.INACTIVE
        )
      : locations || [];
    return orderBy(
      map(filteredLocations, (location) => ({
        locationId: location.id,
        locationName: location.name,
        locationDistrict: location.district,
        locationIdNumber: location.idNumber,
        locationStatus: location.status,
        locationNickname: location.nickname,
        locationBackgroundColor: location.backgroundColor,
        locationMeetUpPoint: location.meetUpPoint,
        isNeedToolBag: location.isLocationAssistantNeedToolsBag,
      })),
      [
        (item) => {
          const index = findIndex(
            preferredOrder,
            (district) => item.locationDistrict === district
          );
          return index === -1 ? preferredOrder.length : index;
        },
        'locationStatus',
        'locationIdNumber',
      ],
      ['asc', 'asc', 'asc']
    );
  }, [hideInactiveLocations, locations]);

  const renderEmployeeOptions = useCallback(
    (date: string, locationId: string) => {
      return orderBy(
        map(employees, (employee) => {
          const reportStatus = getReportStatus(employee.id, date);
          const isAvailable = reportStatus === 'AVAILABLE';

          return {
            name: `${employee.idNumber}${employee.name}`,
            isAvailable,
            label: (
              <div className="flex items-center">
                {isAvailable && (
                  <span
                    className="flex-shrink-0 w-3 h-3 mr-2 rounded-full"
                    style={{ backgroundColor: 'lightgreen' }}
                  />
                )}
                <span className="overflow-hidden text-ellipsis">
                  {employee.idNumber} - {employee.name}
                </span>
              </div>
            ),
            value: employee.id,
          };
        }),
        ['isAvailable', 'name'],
        ['desc', 'asc']
      );
    },
    [employees, getReportStatus]
  );

  const renderLocationOptions = useCallback(
    (employeeId?: string) => {
      const historyLocationIds = employeeId
        ? employeeLocationHistoryMap.get(employeeId)
        : undefined;

      const options = map(locations, (location) => {
        const hasHistory = historyLocationIds?.has(location.id) || false;

        return {
          name: `${location.idNumber}${location.nickname || location.name}`,
          idNumber: location.idNumber,
          hasHistory,
          label: (
            <div className="flex items-center">
              <span
                className="flex-shrink-0 w-3 h-3 rounded-full"
                style={{
                  backgroundColor: hasHistory ? '#22c55e' : '#d1d5db',
                }}
              />
              <span
                className={`ml-2 flex-shrink text-ellipsis overflow-hidden ${
                  location.status === COOPERATION_STATE.INACTIVE
                    ? 'text-red-500'
                    : ''
                }`}
              >
                {location.idNumber} - {location.nickname || location.name}
                {location.status === COOPERATION_STATE.INACTIVE &&
                  `(${COOPERATION_STATE_LABEL[location.status]})`}
              </span>
            </div>
          ),
          value: location.id,
        };
      });

      // 優先排序：1. 有歷史排班記錄的場域排在前面 2. 依照編號排序
      return orderBy(options, ['hasHistory', 'idNumber'], ['desc', 'asc']);
    },
    [locations, employeeLocationHistoryMap]
  );

  const onExportButtonClick = (ref: RefObject<any>) => {
    if (!ref || !ref.current) return;

    const tRef = (ref.current as any).childNodes[0].querySelector('table');
    html2canvas(tRef).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `班表_${dayjs(schedule?.startTime).format(
        'YYYY-MM-DD'
      )}至${dayjs(schedule?.endTime).format('YYYY-MM-DD')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <div className="flex flex-col items-stretch gap-4">
      <Breadcrumb
        items={[
          {
            title: <HomeOutlined />,
            href: '/',
          },
          {
            title: 'PT班表管理',
            href: '/schedules/pt',
          },
          {
            title: '班表日程表',
          },
        ]}
      />
      <div className="flex items-center justify-between">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          返回班表列表
        </Button>
        <span className="text-lg font-bold">
          班表日程表: {dayjs(schedule?.startTime).format('YYYY-MM-DD')} 至{' '}
          {dayjs(schedule?.endTime).format('YYYY-MM-DD')}
        </span>
      </div>

      <Card>
        <div className="flex items-center gap-4">
          <Checkbox
            onChange={(e) => setShowHolidays(e.target.checked)}
            checked={showHolidays}
          >
            顯示假日
          </Checkbox>
          <Checkbox
            onChange={(e) => setHideInactiveLocations(e.target.checked)}
            checked={hideInactiveLocations}
          >
            隱藏已終止合作大樓
          </Checkbox>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          type="card"
          destroyInactiveTabPane
          items={[
            {
              label: '員工視圖',
              key: 'employee',
              children: (
                <>
                  <div>
                    <Button
                      type="primary"
                      onClick={() => onExportButtonClick(employeeTableRef)}
                      loading={isExporting}
                    >
                      匯出班表
                    </Button>
                  </div>
                  <Table
                    ref={employeeTableRef}
                    rowKey="employeeId"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="small"
                    loading={isLoading}
                    columns={[
                      {
                        title: '員工',
                        dataIndex: 'employeeName',
                        fixed: 'left' as const,
                        width: 120,
                        render: (text: string, record) => (
                          <div className="flex items-center justify-between">
                            <span>
                              {record.employeeIdNumber
                                ? `${record.employeeIdNumber} - ${record.employeeName}`
                                : record.employeeName}
                            </span>
                            <Button
                              size="small"
                              type="link"
                              onClick={() =>
                                handleOpenBatchModal(record.employeeId)
                              }
                            >
                              批次
                            </Button>
                          </div>
                        ),
                      },
                      {
                        title: '是否已報班',
                        dataIndex: 'isReported',
                        fixed: 'left' as const,
                        align: 'center' as const,
                        width: 110,
                        filterMultiple: false,
                        filters: [
                          { text: '已報班', value: true },
                          { text: '未報班', value: false },
                        ],
                        onFilter: (value: any, record: any) =>
                          record.isReported === value,
                        render: (value: boolean) => (value ? '✔️' : '❌'),
                      },
                      ...dateRange.map(
                        ({ dateString, formattedDate, isWeekend }) => ({
                          align: 'center' as any,
                          hidden: !showHolidays && isWeekend,
                          title: (
                            <span
                              className={`${isWeekend ? 'text-red-500' : ''}`}
                            >
                              {formattedDate}
                            </span>
                          ),
                          dataIndex: dateString,
                          width: 160,
                          render: (text: string, record: any) => {
                            const reportStatus = getReportStatus(
                              record.employeeId,
                              dateString
                            );
                            const isAvailable = reportStatus === 'AVAILABLE';

                            if (!isAvailable) {
                              return <span className="text-gray-400">---</span>;
                            }

                            return (
                              <Select
                                value={record[dateString] || undefined}
                                allowClear
                                showSearch
                                optionFilterProp="name"
                                style={{
                                  width: '100%',
                                }}
                                onChange={(value) => {
                                  handleEmployeeShiftUpdate(
                                    record.employeeId,
                                    dateString,
                                    value
                                  );
                                }}
                                options={renderLocationOptions(
                                  record.employeeId
                                )}
                              />
                            );
                          },
                        })
                      ),
                    ]}
                    dataSource={employeeDataSource}
                  />
                </>
              ),
            },
            {
              label: '地點日曆視圖',
              key: 'location',
              children: (
                <>
                  <div>
                    <Button
                      type="primary"
                      onClick={() => onExportButtonClick(locationTableRef)}
                      loading={isExporting}
                    >
                      匯出班表
                    </Button>
                  </div>
                  <Table
                    ref={locationTableRef}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    rowClassName={(_, index) =>
                      _.locationStatus === COOPERATION_STATE.INACTIVE
                        ? 'bg-gray-200'
                        : ''
                    }
                    bordered
                    size="small"
                    rowKey="locationId"
                    loading={isLoading}
                    dataSource={locationDataSource}
                    columns={[
                      {
                        title: '服務大樓',
                        dataIndex: 'locationName',
                        fixed: 'left' as const,
                        width: 140,
                        className: 'bg-red-100',
                        render: (text: string, record) => (
                          <div className="flex items-center">
                            {record.locationBackgroundColor && (
                              <span
                                className="flex-shrink-0 w-3 h-3 mr-2 rounded-full"
                                style={{
                                  backgroundColor:
                                    record.locationBackgroundColor,
                                }}
                              />
                            )}
                            <span
                              className={
                                record.locationStatus ===
                                COOPERATION_STATE.INACTIVE
                                  ? 'text-red-500'
                                  : ''
                              }
                            >
                              {record.locationIdNumber} -{' '}
                              {record.locationNickname || record.locationName}
                              {record.locationStatus ===
                                COOPERATION_STATE.INACTIVE && (
                                <span>
                                  (
                                  {
                                    COOPERATION_STATE_LABEL[
                                      record.locationStatus
                                    ]
                                  }
                                  )
                                </span>
                              )}
                            </span>
                          </div>
                        ),
                      },
                      ...dateRange.map(
                        ({ dateString, formattedDate, isWeekend }) => ({
                          align: 'center' as any,
                          hidden: !showHolidays && isWeekend,
                          title: (
                            <span
                              className={`${isWeekend ? 'text-red-500' : ''}`}
                            >
                              {formattedDate}
                            </span>
                          ),
                          dataIndex: dateString,
                          width: 140,
                          render: (
                            shifts: FindScheduleByIdResult['shifts'],
                            record: any
                          ) => {
                            const locationShifts = getShiftsByLocationAndTime(
                              record.locationId,
                              dateString
                            );
                            const currentEmployeeIds = map(
                              orderBy(
                                map(locationShifts, (s) => ({
                                  ...employeeDict[s.employeeId],
                                  ...s,
                                })),
                                ['employeeIdNumber'],
                                ['asc']
                              ),
                              (shift) => shift.employeeId
                            );

                            const isNeedMoreStaff = getIsWaitingForArrange(
                              dateString,
                              record.locationId
                            );

                            return (
                              <div
                                className="flex flex-col items-stretch gap-2"
                                style={{
                                  backgroundColor: isNeedMoreStaff
                                    ? '#fce7f3'
                                    : 'transparent',
                                }}
                              >
                                <div className="flex items-center mb-2 gap-2">
                                  <Checkbox
                                    checked={isNeedMoreStaff}
                                    onChange={(e) =>
                                      handleNeedMoreStaffChange(
                                        record.locationId,
                                        dateString,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <span
                                    className="text-xs text-gray-600 cursor-pointer select-none"
                                    onClick={() =>
                                      handleNeedMoreStaffChange(
                                        record.locationId,
                                        dateString,
                                        !isNeedMoreStaff
                                      )
                                    }
                                  >
                                    需要補人
                                  </span>
                                </div>
                                <Select
                                  mode="multiple"
                                  value={currentEmployeeIds}
                                  allowClear
                                  showSearch
                                  optionFilterProp="name"
                                  onChange={(values: string[]) => {
                                    handleLocationShiftsUpdate(
                                      record.locationId,
                                      dateString,
                                      values
                                    );
                                  }}
                                  options={renderEmployeeOptions(
                                    dateString,
                                    record.locationId
                                  )}
                                />
                                {record.isNeedToolBag &&
                                  currentEmployeeIds.length > 0 && (
                                    <div className="flex flex-wrap mt-1 gap-1">
                                      {currentEmployeeIds.map((id) => {
                                        const toolBagOwner = find(
                                          toolBagOwners,
                                          (owner) =>
                                            owner.locationId ===
                                              record.locationId &&
                                            owner.time === dateString
                                        );
                                        const isPrimaryOwner =
                                          toolBagOwner?.employeeId === id;
                                        const isSecondaryOwner =
                                          toolBagOwner?.secondaryEmployeeId ===
                                          id;

                                        const getTooltipTitle = () => {
                                          if (isPrimaryOwner) {
                                            return '主要工具包負責人（點擊取消）';
                                          }
                                          if (isSecondaryOwner) {
                                            return '副工具包負責人（點擊取消）';
                                          }
                                          if (toolBagOwner?.employeeId) {
                                            return '點擊設為副工具包負責人（帶班新人）';
                                          }
                                          return '點擊設為主要工具包負責人';
                                        };

                                        const handleClick = () => {
                                          if (isPrimaryOwner) {
                                            // 取消主要負責人：如果有副負責人，升級為主負責人；否則刪除記錄
                                            if (
                                              toolBagOwner?.secondaryEmployeeId
                                            ) {
                                              createToolBagOwner({
                                                employeeId:
                                                  toolBagOwner.secondaryEmployeeId,
                                                locationId: record.locationId,
                                                time: dateString,
                                                secondaryEmployeeId: undefined,
                                              });
                                            } else {
                                              createToolBagOwner({
                                                employeeId: undefined,
                                                locationId: record.locationId,
                                                time: dateString,
                                              });
                                            }
                                          } else if (isSecondaryOwner) {
                                            // 取消副負責人
                                            createToolBagOwner({
                                              employeeId:
                                                toolBagOwner?.employeeId,
                                              locationId: record.locationId,
                                              time: dateString,
                                              secondaryEmployeeId: undefined,
                                            });
                                          } else if (toolBagOwner?.employeeId) {
                                            // 已有主負責人，設為副負責人
                                            createToolBagOwner({
                                              employeeId:
                                                toolBagOwner.employeeId,
                                              locationId: record.locationId,
                                              time: dateString,
                                              secondaryEmployeeId: id,
                                            });
                                          } else {
                                            // 設為主要負責人
                                            createToolBagOwner({
                                              employeeId: id,
                                              locationId: record.locationId,
                                              time: dateString,
                                            });
                                          }
                                        };

                                        const getTagColor = () => {
                                          if (isPrimaryOwner) return 'gold';
                                          if (isSecondaryOwner) return 'blue';
                                          return 'default';
                                        };

                                        const getTagIcon = () => {
                                          if (isPrimaryOwner) return '👜 ';
                                          if (isSecondaryOwner) return '🎒 ';
                                          return '';
                                        };

                                        return (
                                          <Tooltip
                                            key={id}
                                            title={getTooltipTitle()}
                                          >
                                            <Tag
                                              color={getTagColor()}
                                              className="cursor-pointer select-none"
                                              onClick={handleClick}
                                            >
                                              {getTagIcon()}
                                              {employeeDict[id]?.name}
                                            </Tag>
                                          </Tooltip>
                                        );
                                      })}
                                    </div>
                                  )}
                              </div>
                            );
                          },
                        })
                      ),
                    ]}
                  />
                </>
              ),
            },
            {
              label: '檢視班表',
              key: 'view',
              children: (
                <>
                  <div className="flex items-center mb-2 gap-4">
                    <Button
                      type="primary"
                      onClick={() => onExportButtonClick(shiftTableRef)}
                      loading={isExporting}
                    >
                      匯出班表
                    </Button>
                    <Checkbox
                      checked={showExtraColumns}
                      onChange={(e) => setShowExtraColumns(e.target.checked)}
                    >
                      顯示服務區域/集合地點
                    </Checkbox>
                  </div>
                  <Table
                    ref={shiftTableRef}
                    pagination={false}
                    bordered
                    size="small"
                    rowKey="locationId"
                    loading={isLoading}
                    dataSource={locationDataSource}
                    scroll={{ x: 1500 }}
                    columns={[
                      {
                        title: '服務大樓',
                        dataIndex: 'locationName',
                        fixed: 'left' as const,
                        width: 160,
                        onCell: (record) => {
                          const { locationBackgroundColor } = record;

                          if (!locationBackgroundColor) return {};

                          return {
                            style: {
                              backgroundColor:
                                locationBackgroundColor || undefined,
                              color: getTextColorFromHexBg(
                                locationBackgroundColor
                              ),
                            },
                          };
                        },
                        render: (text: string, record) => (
                          <span>
                            {record.locationIdNumber} -{' '}
                            {record.locationNickname || record.locationName}
                          </span>
                        ),
                      },
                      {
                        align: 'center',
                        title: '服務區域',
                        dataIndex: 'locationDistrict',
                        width: 70,
                        hidden: !showExtraColumns,
                      },
                      {
                        title: '集合地點',
                        width: 140,
                        dataIndex: 'locationMeetUpPoint',
                        hidden: !showExtraColumns,
                      },
                      ...map(
                        dateRange,
                        ({ dateString, formattedDate, isWeekend }) => ({
                          align: 'center' as any,
                          hidden: !showHolidays && isWeekend,
                          title: (
                            <span
                              className={`${isWeekend ? 'text-red-500' : ''}`}
                            >
                              {formattedDate}
                            </span>
                          ),
                          dataIndex: dateString,
                          width: 120,
                          onCell: (record: any) => ({
                            style: {
                              padding: 0,
                              verticalAlign: 'top',
                            },
                          }),
                          render: (
                            shifts: FindScheduleByIdResult['shifts'],
                            record: any
                          ) => {
                            const locationShifts = getShiftsByLocationAndTime(
                              record.locationId,
                              dateString
                            );
                            const currentEmployeeIds = map(
                              orderBy(
                                map(locationShifts, (s) => ({
                                  ...employeeDict[s.employeeId],
                                  ...s,
                                })),
                                ['employeeIdNumber'],
                                ['asc']
                              ),
                              (shift) => shift.employeeId
                            );

                            const isNeedMoreStaff = getIsWaitingForArrange(
                              dateString,
                              record.locationId
                            );

                            return (
                              <div
                                className="flex flex-col items-stretch p-2"
                                style={{
                                  backgroundColor: isNeedMoreStaff
                                    ? '#fce7f3'
                                    : 'transparent',
                                  minHeight: '60px',
                                }}
                              >
                                {isNeedMoreStaff && (
                                  <div className="mb-1 text-xs text-red-500">
                                    需要補人
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {map(currentEmployeeIds, (id) => {
                                    const toolBagOwner = find(
                                      toolBagOwners,
                                      (owner) =>
                                        owner.locationId ===
                                          record.locationId &&
                                        owner.time === dateString
                                    );
                                    const isPrimaryOwner =
                                      toolBagOwner?.employeeId === id;
                                    const isSecondaryOwner =
                                      toolBagOwner?.secondaryEmployeeId === id;

                                    const getTagColor = () => {
                                      if (isPrimaryOwner) return 'gold';
                                      if (isSecondaryOwner) return 'blue';
                                      return 'default';
                                    };

                                    const getToolBagIcon = () => {
                                      if (isPrimaryOwner) return ' 👜';
                                      if (isSecondaryOwner) return ' 🎒';
                                      return '';
                                    };

                                    return (
                                      <Tag
                                        key={id}
                                        color={getTagColor()}
                                        style={{ margin: 0 }}
                                      >
                                        {`${employeeDict[id]?.name}${getToolBagIcon()}`}
                                      </Tag>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          },
                        })
                      ),
                    ]}
                  />
                </>
              ),
            },
            {
              label: '班表提交狀況',
              key: 'report',
              children: <PTReportShiftTable showHolidays={showHolidays} />,
            },
          ]}
        />
      </Card>

      <Modal
        title={`批次設定排班 - ${batchEmployeeId ? employeeDict[batchEmployeeId]?.name : ''}`}
        open={batchModalVisible}
        onOk={handleBatchConfirm}
        onCancel={handleBatchCancel}
        okText="確認設定"
        cancelText="取消"
        width={600}
      >
        <div className="flex flex-col gap-4">
          <div>
            <div className="mb-2 font-medium">選擇地點：</div>
            <Select
              value={batchLocationId}
              allowClear
              showSearch
              optionFilterProp="name"
              placeholder="選擇地點（留空則清除排班）"
              style={{ width: '100%' }}
              onChange={(value) => setBatchLocationId(value)}
              options={renderLocationOptions(batchEmployeeId || undefined)}
            />
          </div>
          <div>
            <div className="mb-2 font-medium">選擇日期：</div>
            <div className="flex mb-2 gap-2">
              <Button
                size="small"
                onClick={() =>
                  setBatchSelectedDates(
                    batchAvailableDates.map(({ dateString }) => dateString)
                  )
                }
              >
                全選
              </Button>
              <Button size="small" onClick={() => setBatchSelectedDates([])}>
                清除
              </Button>
            </div>
            <Checkbox.Group
              value={batchSelectedDates}
              onChange={(values) => setBatchSelectedDates(values as string[])}
            >
              <div className="grid grid-cols-4 gap-2">
                {batchAvailableDates.map(
                  ({ dateString, formattedDate, isWeekend }) => (
                    <Checkbox key={dateString} value={dateString}>
                      <span className={isWeekend ? 'text-red-500' : ''}>
                        {formattedDate}
                      </span>
                    </Checkbox>
                  )
                )}
              </div>
            </Checkbox.Group>
            {batchAvailableDates.length === 0 && (
              <div className="text-gray-400">該員工沒有可報班的日期</div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
